import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from "react";
import type { getTheme } from "../../config/theme";

type ButtonVariant = "primary" | "secondary" | "tertiary";
type ThemeColors = ReturnType<typeof getTheme>["colors"];

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  theme: ThemeColors;
}

// Theme is applied via inline styles (not Tailwind `dark:` classes) because
// the app's ThemeProvider deliberately never toggles a `dark` class on the
// document — see src/context/ThemeContext.tsx.
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", theme, className = "", style, ...props }, ref) => {
    const base =
      "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50";

    const variantStyle: CSSProperties =
      variant === "primary"
        ? { background: theme.primary, color: "#FFFFFF" }
        : variant === "secondary"
          ? { background: "transparent", color: theme.text, border: `1px solid ${theme.border}` }
          : { background: "transparent", color: theme.textMuted };

    return (
      <button ref={ref} className={`${base} ${className}`} style={{ ...variantStyle, ...style }} {...props} />
    );
  },
);
Button.displayName = "Button";

export default Button;
